/**
 * Maps a URL pathname (as seen by react-router, e.g. "/member/AMC/payment")
 * to a readable, partner/usertype-agnostic action name suffix used for the
 * `route.viewed` journey event's `screenName` metadata (e.g. "memberlookup",
 * "payment", "serviceselection"). This keeps Datadog action names readable
 * and searchable (e.g. `journey-monitoring_<id>_route.memberlookup`) instead
 * of one distinct name per partner/usertype/path combination.
 *
 * Matching is based on the trailing path segment (ignoring `:usertype` and
 * `:partnercode`), mirroring the route definitions in `App.js`. Query
 * parameters are inspected only for the `/lookup` route, which is used both
 * for the standard member-lookup flow and, when `ANI` + `conversationId`
 * query params are present, an ANI-based (ANI = Automatic Number
 * Identification, i.e. caller phone lookup) lookup flow.
 *
 * Unmapped/unknown paths fall back to "unknown"; the raw path is preserved
 * separately in event metadata by the caller for debugging.
 */

// Segments that map 1:1 to a screen name, keeping the exact casing used in
// App.js route definitions.
const SEGMENT_TO_SCREEN_NAME: Record<string, string> = {
  memberlookup: "memberlookup",
  triage: "triage",
  "triage-steps": "triage-steps",
  "other-triage": "other-triage",
  "mechanical-issue-triage": "mechanical-issue-triage",
  "incident-report": "incident-report",
  "service-time": "service-time",
  disablementlocation: "disablementLocation",
  location: "location",
  "confirm-vehicle": "confirm-vehicle",
  "add-vehicle": "add-vehicle",
  "vehicle-search": "vehicle-search",
  payment: "payment",
  wd: "wd",
  feedback: "feedback",
  ServiceTransition: "ServiceTransition",
  sitedown: "sitedown",
  ScheduleService: "ScheduleService",
  duplicaterescue: "duplicaterescue",
  CallUs: "CallUs",
  getStarted: "getStarted",
  benefitsSelection: "benefitsSelection",
  home: "serviceselection",
};

const UNKNOWN_SCREEN_NAME = "unknown";

/**
 * @param pathname - e.g. location.pathname from react-router
 * @param search - e.g. location.search (starts with "?"), used only to
 *   disambiguate the /lookup route's ANI-lookup variant.
 * @returns screen name suffix for the route.viewed action name.
 */
export const getScreenNameForPath = (pathname?: string | null, search = ""): string => {
  if (!pathname || typeof pathname !== "string") {
    return UNKNOWN_SCREEN_NAME;
  }

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) {
    // "/" root redirect
    return "memberlookup";
  }

  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];

  // "/wd/:linkType/:partnerCode/:uuid/:usageId/:language?" — standalone
  // rescue-tracker link, not under :usertype/:partnercode, so match on the
  // leading segment rather than the trailing one.
  if (firstSegment === "wd") {
    return "wd";
  }

  // "/appointment-confirmation" — top-level route, no :usertype prefix.
  if (firstSegment === "appointment-confirmation") {
    return "appointment-confirmation";
  }

  // "/lookup" is ambiguous: default member-lookup flow vs. ANI-based lookup
  // (query params ANI + conversationId both present).
  if (lastSegment === "lookup") {
    const params = new URLSearchParams(search || "");
    if (params.get("ANI") && params.get("conversationId")) {
      return "ani_lookup";
    }
    return "memberlookup";
  }

  // Landing routes with no trailing segment beyond usertype(/partnercode):
  // "/:usertype/" and "/:usertype/:partnercode" (exact) both render the
  // member-lookup entry point. ("/:usertype/:partnercode/home", length 3,
  // is handled below via the SEGMENT_TO_SCREEN_NAME "home" mapping.)
  if (segments.length === 1 || segments.length === 2) {
    return "memberlookup";
  }

  // "/help/:type" — dynamic trailing segment, match on the segment before it.
  if (segments.length >= 2 && segments[segments.length - 2] === "help") {
    return "help";
  }

  if (Object.prototype.hasOwnProperty.call(SEGMENT_TO_SCREEN_NAME, lastSegment)) {
    return SEGMENT_TO_SCREEN_NAME[lastSegment];
  }

  // The disablement-location route's casing varies by caller
  // ("disablementlocation" vs "DisablementLocation"); match case-insensitively.
  const lowerLastSegment = lastSegment.toLowerCase();
  if (lowerLastSegment === "disablementlocation") {
    return SEGMENT_TO_SCREEN_NAME.disablementlocation;
  }

  return UNKNOWN_SCREEN_NAME;
};
